import { types, getParent } from 'mobx-state-tree'
import { format } from 'date-fns'
import { CustomerReference } from './Customer'
import { JobReference } from './Job'
import shortid from 'shortid'
// import localforage from 'localforage';

const DEFAULT_DATE_FORMAT = 'D/MM/YY'

// const store = localforage.createInstance({
//   name: 'workOrders'
// })

export default types.model({
  id: types.identifier(types.string),
  workOrderNumber: types.maybe(types.number),
  dateOrdered: types.Date,
  customer: types.maybe(CustomerReference),
  job: types.maybe(JobReference),
  orderNumber: '',
  qty: 0,
  finish: '',
  specialInstructions: '',
  deliveryDetails: '',
  isUrgent: false,
  isOnHold: false,
  isWaiting: false,
  statusNote: '',
  dateMaterialRequired: types.maybe(types.Date),
  dateMaterialOrdered: types.maybe(types.Date),
  dateMaterialInStock: types.maybe(types.Date),
  dateRequiredAtFinishers: types.maybe(types.Date),
  dateAtFinishers: types.maybe(types.Date),
  dateRequiredReady: types.maybe(types.Date),
  dateReady: types.maybe(types.Date),
  dateDeliveryRequired: types.maybe(types.Date),
  dateDelivered: types.maybe(types.Date),
}).preProcessSnapshot(snapshot => {
  const copy = Object.assign(snapshot)
  if (!copy.id) copy.id = shortid.generate()
  if (!copy.dateOrdered) copy.dateOrdered = new Date()
  return copy
}).views(self => ({
  get isMaterialRequired() { return self.dateMaterialRequired ? true : false },
  get isMaterialOrdered() { return self.dateMaterialOrdered ? true : false },
  get isMaterialInStock() { return self.dateMaterialInStock ? true : false },
  get isRequiredAtFinishers() { return self.dateRequiredAtFinishers ? true : false },
  get isAtFinishers() { return self.dateAtFinishers ? true : false },
  get isRequiredReady() { return self.dateRequiredReady ? true : false },
  get isReady() { return self.dateReady ? true : false },
  get isDeliveryRequired() { return self.dateDeliveryRequired ? true : false },
  get isDelivered() { return self.dateDelivered ? true : false },
  get hasWorkOrderNumber() { return self.workOrderNumber ? true : false },
  get dateOrderedAsString() { return self.dateOrdered ? format(self.dateOrdered, DEFAULT_DATE_FORMAT) : '' },
  get dateMaterialRequiredAsString() { return self.dateMaterialRequired ? format(self.dateMaterialRequired, DEFAULT_DATE_FORMAT) : '' },
  get dateRequiredAtFinishersAsString() { return self.dateRequiredAtFinishers ? format(self.dateRequiredAtFinishers, DEFAULT_DATE_FORMAT) : '' },
  get dateRequiredReadyAsString() { return self.dateRequiredReady ? format(self.dateRequiredReady, DEFAULT_DATE_FORMAT) : '' },
  get dateDeliveryRequiredAsString() { return self.dateDeliveryRequired ? format(self.dateDeliveryRequired, DEFAULT_DATE_FORMAT) : '' },
  get asString() {
    return 'Work Order #' + self.workOrderNumber + ' '
    + self.job.asString() + ' x' + self.qty + ' for '
    + self.customer.name
  },
  get customerAsString() {
    return self.customer ? self.customer.name : ''
  },
  get jobAsString() {
    return self.job ? self.job.jobNumber ? self.job.jobNumber + ' - ' + self.job.description : self.job.description : ''
  },
  get isValid() {
    if (!(self.dateOrdered instanceof Date)) return false
    if (!self.customer || !self.job) return false
    if (!self.job.jobNumber) return false
    return self.qty > 0
  }
})).actions(self => ({
  save() {
    // store.setItem(self.id, getSnapshot(self))
    getParent(self, 2).saveItem(self)
  },
  afterAttach() {
    if (!self.workOrderNumber) {
      self.workOrderNumber = getParent(self, 2).nextWorkOrderNumber()
    } 
  },
  setDateOrdered(date) {
    if (date instanceof Date) self.dateOrdered = date
  },
  setDateMaterialRequired(date) {
    if (date instanceof Date) self.dateMaterialRequired = date
  },
  unsetDateMaterialRequired() {
    self.dateMaterialRequired = null
  },
  setDateRequiredAtFinishers(date) {
    if (date instanceof Date) self.dateRequiredAtFinishers = date
  },
  unsetDateRequiredAtFinishers() {
    self.dateRequiredAtFinishers = null
  },
  setDateRequiredReady(date) {
    if (date instanceof Date) self.dateRequiredReady = date
  },
  unsetDateRequiredReady() {
    self.dateRequiredReady = null
  },
  setDateDeliveryRequired(date) {
    if (date instanceof Date) self.dateDeliveryRequired = date
  },
  unsetDateDeliveryRequired() {
    self.dateDeliveryRequired = null
  },
  toggleIsUrgent() {
    self.isUrgent = !self.isUrgent
    if (self.isUrgent) {
      self.isOnHold = false
      self.isWaiting = false
    }
  },
  toggleIsOnHold() {
    self.isOnHold = !self.isOnHold
    if (self.isOnHold) {
      self.isUrgent = false
      self.isWaiting = false
    }
  },
  toggleIsWaiting() {
    self.isWaiting = !self.isWaiting
    if (self.isWaiting) {
      self.isUrgent = false
      self.isOnHold = false
    }
  },
  toggleIsMaterialOrdered() {
    if (self.isMaterialOrdered) {
      self.dateMaterialOrdered = null
    } else {
      self.dateMaterialOrdered = new Date()
    }
  },
  toggleIsMaterialInStock() {
    if (self.isMaterialInStock) {
      self.dateMaterialInStock = null
    } else {
      self.dateMaterialInStock = new Date()
    }
  },
  toggleIsAtFinishers() {
    if (self.isAtFinishers) {
      self.dateAtFinishers = null
    } else {
      self.dateAtFinishers = new Date()
    }
  },
  toggleIsReady() {
    if (self.isReady) {
      self.dateReady = null
    } else {
      self.dateReady = new Date()
    }
  },
  toggleIsDelivered() {
    if (self.isDelivered) {
      self.dateDelivered = null
    } else {
      self.dateDelivered = new Date()
    }
  },
  setWorkOrderNumber(number) {
    self.workOrderNumber = number
  },
  setCustomer(customer) {
    self.customer = customer
  },
  setOrderNumber(string) {
    self.orderNumber = string
  },
  setJob(job) {
    self.job = job
  },
  setQty(number) {
    self.qty = number
  },
  setFinish(value) {
    self.finish = value
  },
  setSpecialInstructions(value) {
    self.specialInstructions = value
  },
  setDeliveryDetails(value) {
    self.deliveryDetails = value
  },
  setStatusNote(value) {
    self.statusNote = value
  },
}))


// WEBPACK FOOTER //
// ./src/models/WorkOrder.js